import { useCallback, useRef, useState } from 'react';
import { ModalLayout, SearchableSelect, TextArea, Input, Button, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useWorkQueueContext } from '../../context/useWorkQueueContext.js';
import { useWorkspaceUsers } from '../../hooks/useWorkspaceUsers.js';
import { HelpCircle } from 'lucide-react';

/**
 * Ask a targeted question about an event. V1 limits the response recipient to a
 * single person. Persists via {@link createAskQuestion} mock (CT-4867 — no API).
 */
function AskQuestionModal({ isOpen, event, onClose }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { createQuestion } = useWorkQueueContext();
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipient, setRecipient] = useState(null);
  const [question, setQuestion] = useState('');
  const [context, setContext] = useState('');
  const [dueBy, setDueBy] = useState('');
  const submittingRef = useRef(false);
  // Real workspace directory (CT-5094) — recipients are real users, not mock.
  // `[recipient?.id]` (selectedIds) keeps a just-picked recipient from vanishing
  // from `options` when the search that surfaced them clears and refetches.
  const { users, loading: usersLoading, error: usersError } = useWorkspaceUsers(
    isOpen,
    recipientSearch,
    recipient?.id ? [recipient.id] : undefined,
  );

  const canSubmit = recipient && question.trim().length > 0;

  // Cancel / overlay / Esc / X all resolve to `onClose` directly (the modal stays
  // mounted, per ModalLayout convention), so the picker's typed query survives a
  // close and refetches on reopen instead of showing the honest unsearched page.
  const handleClose = useCallback(() => {
    setRecipientSearch('');
    onClose();
  }, [onClose]);

  const handleSubmit = async () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    try {
      await createQuestion({
        sourceEvent: event,
        recipient,
        question: question.trim(),
        context: context.trim(),
        dueBy,
      });
      toast.success({
        title: t('ask.sentTitle'),
        message: t('ask.sentMessage', { recipient: recipient.name }),
      });
      setRecipient(null);
      setQuestion('');
      setContext('');
      setDueBy('');
      handleClose();
    } finally {
      submittingRef.current = false;
    }
  };

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      useBodyPortal
      titlePrefix={<HelpCircle size={20} className="text-sky-500" />}
      breadcrumb={event ? `${event.id} · ${event.refLabel ?? ''}` : undefined}
      title={t('ask.title')}
      subtitle={t('ask.subtitle')}
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-content">{t('ask.recipientLabel')}</label>
          <SearchableSelect
            options={users}
            value={recipient?.id ?? null}
            onSelect={setRecipient}
            onChange={setRecipientSearch}
            showAvatar={false}
            disabled={usersLoading || Boolean(usersError)}
            placeholder={usersLoading ? t('team.reassignLoading') : t('ask.recipientPlaceholder')}
            searchPlaceholder={t('ask.recipientSearch')}
          />
          {usersError && <p className="mt-1.5 text-xs text-rose-500">{t('team.reassignLoadError')}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-content">{t('ask.questionLabel')}</label>
          <TextArea
            rows={4}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t('ask.questionPlaceholder')}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-content">
            {t('ask.contextLabel')}
          </label>
          <TextArea
            rows={3}
            value={context}
            onChange={(changeEvent) => setContext(changeEvent.target.value)}
            placeholder={t('ask.contextPlaceholder')}
          />
        </div>
        <Input
          type="date"
          label={t('ask.dueByLabel')}
          value={dueBy}
          onChange={(changeEvent) => setDueBy(changeEvent.target.value)}
        />
      </div>

      <div className="flex justify-end gap-2 border-t border-edge px-6 py-4">
        <Button variant="secondary" size="sm" onClick={handleClose}>
          {t('actions.cancel')}
        </Button>
        <Button variant="primary" size="sm" disabled={!canSubmit} onClick={handleSubmit}>
          {t('ask.send')}
        </Button>
      </div>
    </ModalLayout>
  );
}

export default AskQuestionModal;
